import { DialogId, IntentId, NlpIntent, NlpModelId } from '@feedyou/schema'
import { AnyObject, KnowledgeBaseSettings } from '@feedyou/utils'

import { StepSettingsSubset } from '../../constants/stepSettings'
import { NlpLogGroup, NlpLogDbPartitionDescriptor } from '../../components/nlp/TrainingTab/types'

export type ModalDescriptor =
  | { type: 'nlp_settings'; openSection: 'form' | 'json' | 'stepNlpConfigs' }
  | { type: 'bot_settings'; openSection: 'form' | 'json' | 'general' | 'permissions' | 'wizard' }
  | {
      type: 'intent_settings'
      openSection: 'form' | 'json'
      modelId: NlpModelId
      intentId: IntentId
    }
  | {
      type: 'step_settings'
      openSection: 'form' | 'json'
      index?: number
      stepOrSubstep: AnyObject
      dialogId: DialogId
      substepType?: StepSettingsSubset
      modalDataType?: 'mutations'
    }
  | {
      type: 'channels_settings'
      openSection: 'form' | 'json'
    }
  | { type: 'features_settings'; openSection: 'form' | 'json' }
  | {
      type: 'create_bot_settings'
      openSection: 'form' | 'json'
      creationType: 'create_new' | 'duplicate'
    }
  | {
      type: 'nlp_models_test'
      modelId: NlpModelId
    }
  | {
      type: 'utterance_classifier'
      utterance: string
    }
  | {
      type: 'fuzzy_question_nlp_test'
      stepOrSubstep: AnyObject
    }
  | {
      type: 'knowledge_base_upload_document'
      path: string
      modelId: NlpModelId
      botId: string
    }
  | {
      type: 'unmatched_utterance_settings'
      intent: NlpIntent
      logGroup: NlpLogGroup
      llmChatService: string
      partition: NlpLogDbPartitionDescriptor
    }
  | {
      type: 'knowledge_base_path_settings'
      path: string
      settings?: KnowledgeBaseSettings
      updatePathSettings: (path: string, settings: KnowledgeBaseSettings) => void
    }
  | {
      type: 'add_nlp_model_settings'
      openSection: 'form' | 'json'
    }
  | {
      type: 'add_knowledge_base_settings'
      openSection: 'form' | 'json'
    }
  | {
      type: 'nlp_model_settings'
      openSection: 'form' | 'json'
      modelId: NlpModelId
    }
  | {
      type: 'knowledge_base_settings'
      openSection: 'form' | 'json'
      knowledgeBaseId: string
    }
  | {
      type: 'knowledge_base_create_document'
      path: string
      botId: string
      modelId: NlpModelId
    }

export type ModalType = ModalDescriptor['type']

export type ModalDescriptorForType<T extends ModalType> = Extract<ModalDescriptor, { type: T }>

// At this point, there are no other modal types.
// But this sub-category still needs to be defined, because some operations
// can only be made on it, and could not be made on a general modal
const SETTINGS_MODAL_TYPES = [
  'nlp_settings',
  'step_settings',
  'intent_settings',
  'bot_settings',
  'channels_settings',
  'features_settings',
  'create_bot_settings',
  'nlp_model_settings',
  'add_nlp_model_settings',
  'add_knowledge_base_settings',
  'knowledge_base_settings'
] as const

export type SettingsPopupModal = Extract<
  ModalDescriptor,
  { type: typeof SETTINGS_MODAL_TYPES[number] }
>

export type SettingsPopupOpenSection = SettingsPopupModal['openSection']

export const isSettingsModal = (
  modalDescriptor: ModalDescriptor | null | undefined
): modalDescriptor is SettingsPopupModal => {
  if (!modalDescriptor) {
    return false
  }

  return SETTINGS_MODAL_TYPES.includes(modalDescriptor.type as any)
}
